import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  RefObject,
} from 'react';
import { useDispatch } from 'react-redux';

import { fetchCars, searchCars, filterCars } from '../../../api/carsApi';

// at-65 (BUG-V32): sobraram só as duas ações que NÃO são de filtro. Todas as outras
// (`setSelected*`, `resetFilters`, `setSearchResults`, `setReduxCurrentPage`,
// `setLastSearchQuery`) escreviam na fatia da Recepção a partir desta tela — era o
// vazamento. O estado de filtro e de busca daqui é local.
import {
  resetSearchResults,
  setSortOrderRedux,
} from '../../recepcao/recepcaoReducer';

import StatusHistoryModal from '../../../components/modal/status-history/StatusHistoryModal';
import ResponsiblesHistoryModal from '../../../components/modal/resposibles-history/ResponsiblesHistoryModal';
import CarsReportModal from '../../../components/modal/car-report/CarsReportModal';
import FilterCarModal from '../../../components/modal/filter-car/FilterCarModal';
import ChecklistModal from '../../../components/modal/checklist/ChecklistModal';
import TelaDeControleActionsModal from '../../../components/modal/tela-de-controle-actions-modal/TelaDeControleActionsModal';
import ChecklistHistoryModal from '../../../components/modal/checklist-history/ChecklistHistoryModal';

import { CarClass } from '../../../components/mappings/textMappings';
import { useEsteiras } from '../../../hooks/useEsteiras';
import { useTableColumns } from '../../../hooks/useTableColumns';
import { useIsTi } from '../../../hooks/useUserType';

import SearchIcon from '../../../img/search-icon.svg';
import CarAiro from '../../../img/car-airo.svg';
import CarAssociado from '../../../img/car-associado.svg';
import CarAtual from '../../../img/car-atual.svg';
import CarMobi from '../../../img/car-mobi.svg';
import CarPerdaTotal from '../../../img/car-perda-total.svg';
import CarTerceiro from '../../../img/car-terceiro.svg';
import CarExterno from '../../../img/car-externo.svg';
import CarDiretoria from '../../../img/car-diretoria.svg';
import Moto from '../../../img/moto.svg';
import Filter from '../../../img/filter.svg';
import ArrowDown from '../../../img/arrow-down.svg';
import ArrowLeftOrange from '../../../img/arrow-left-orange.svg';
import ArrowLeftGray from '../../../img/arrow-left-gray.svg';
import ArrowRightOrange from '../../../img/arrow-right-orange.svg';
import ArrowRightGray from '../../../img/arrow-right-gray.svg';

import { getChecklistSessionsByCar, getLatestChecklistSessionByCar } from '../../../api/checklistsApi';
import type { TipoServico } from '../../../components/modal/start-checklist/StartChecklistModal';
import { mensagemDeErro, notify } from '../../../shared/feedback/notify';
import { ServicoInacabado } from '../../../interfaces/AtividadeCarro';

import FullScreenLoader from '../../../components/loading/FullScreenLoader';
import { ehFamiliaEntrada, ehFamiliaSaida } from '../../../shared/checklist-familias';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import '../../../components/como-funciona/help-circle-btn.css';
import './TelaDeControle.css';
import '../../recepcao/pages/Recepcao.css';

interface TelaDeControleProps {
  sidebarRef?: RefObject<HTMLDivElement>;
}

interface FilterParams {
  carClass?: CarClass[];
  carStatus?: string[];
  pendingStatus?: string[];
  approvalStatus?: string | string[];
  damageType?: string[];
}

interface ChecklistAvailability {
  entrada: boolean;
  saida: boolean;
}

const headers = [
  'Placa',
  'Modelo',
  'Colaboradores',
  'Classe',
  'Dias na Oficina',
  'Esteira atual',
  'Esteira pendente',
];

const defaultColumnWidths = [
  110, 170, 180, 150, 120, 150, 170,
];

const EMPTY_FILTERS: Required<Omit<FilterParams, 'approvalStatus'>> & {
  approvalStatus?: string | string[];
} = {
  carClass: [],
  carStatus: [],
  pendingStatus: [],
  approvalStatus: undefined,
  damageType: [],
};

const TelaDeControle: React.FC<TelaDeControleProps> = ({ sidebarRef }) => {
  const dispatch = useDispatch();
  const { labelDe } = useEsteiras(true);
  // O relatório de veículos usa rota exclusiva de ADMIN no backend — oculto para TI (at-25).
  const isTi = useIsTi();
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const [cars, setCars] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [totalCars, setTotalCars] = useState<number>(0);

  // at-65 (BUG-V34/V39): DUAS variáveis, de propósito. `searchQuery` é o rascunho que o
  // usuário digita; `appliedSearchQuery` é o que a lista está mostrando. Antes existia só
  // a primeira, e ela era **dependência do efeito de carga** — então cada tecla digitada
  // disparava uma requisição de busca (sem debounce e sem cancelamento), embora a interface
  // só ofereça Enter e o ícone de lupa. O efeito agora reage ao termo APLICADO.
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [appliedSearchQuery, setAppliedSearchQuery] = useState<string>('');
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [isDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const [isReportModalOpen, setIsReportModalOpen] = useState<boolean>(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState<boolean>(false);
  const [isResponsiblesModalOpen, setIsResponsiblesModalOpen] = useState<boolean>(false);
  const [isChecklistHistoryModalOpen, setIsChecklistHistoryModalOpen] = useState<boolean>(false);
  const [isChecklistModalOpen, setIsChecklistModalOpen] = useState(false);
  const [isActionsModalOpen, setIsActionsModalOpen] = useState<boolean>(false);
  const [checklistCarId, setChecklistCarId] = useState<string | null>(null);
  const [checklistSessionId, setChecklistSessionId] = useState<string | null>(null);
  const [checklistTipoServico, setChecklistTipoServico] = useState<TipoServico>('ENTRADA');
  const [checklistReadOnly, setChecklistReadOnly] = useState(true);
  const [checklistAvailabilityByCarId, setChecklistAvailabilityByCarId] = useState<
    Record<string, ChecklistAvailability>
  >({});
  const [selectedCarId, setSelectedCarId] = useState<string | null>(null);
  const [selectedCarPlate, setSelectedCarPlate] = useState<string>('');

  const [isStatusSidebarOpen] = useState<boolean>(true);

  /**
   * at-65 (BUG-V32): os filtros desta tela são LOCAIS.
   *
   * Antes, a Tela de Controle lia e escrevia a mesma fatia de estado global da Recepção
   * (`state.recepcao.selected*`), então filtro aplicado numa aparecia na outra — duas telas
   * de papéis diferentes (ADMIN/TI × RECEPCAO) compartilhando filtro sem que nada na
   * interface dissesse isso. O estado global continua servindo a Recepção; aqui não se lê
   * nem se escreve mais essa fatia.
   */
  const [activeFilters, setActiveFilters] = useState<FilterParams>({ ...EMPTY_FILTERS });

  const availableClasses: CarClass[] = Object.values(CarClass);

  const headerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const tableScrollRef = useRef<HTMLDivElement | null>(null);

  const totalPages = useMemo(() => {
    const pages = Math.ceil(totalCars / limit);
    return pages > 0 ? pages : 1;
  }, [totalCars, limit]);

  const startCarIndex = totalCars === 0 ? 0 : (currentPage - 1) * limit + 1;
  const endCarIndex = Math.min(currentPage * limit, totalCars);

  const getCarClassIcon = (carClass?: string) => {
    if (carClass === 'Airo Clube') {
      return <img src={CarAiro} alt="Car Airo" width="15" height="15" />;
    }
    if (carClass === 'Associado') {
      return <img src={CarAssociado} alt="Car Associado" width="15" height="15" />;
    }
    if (carClass === 'Carros Atual') {
      return <img src={CarAtual} alt="Car Atual" width="15" height="15" />;
    }
    if (carClass === 'Mobi') {
      return <img src={CarMobi} alt="Car Mobi" width="15" height="15" />;
    }
    if (carClass === 'Perda Total') {
      return <img src={CarPerdaTotal} alt="Car Perda Total" width="15" height="15" />;
    }
    if (carClass === 'Terceiro') {
      return <img src={CarTerceiro} alt="Car Terceiro" width="15" height="15" />;
    }
    if (carClass === 'Carros Externos') {
      return <img src={CarExterno} alt="Car Externo" width="15" height="15" />;
    }
    if (carClass === 'Moto') {
      return <img src={Moto} alt="Moto" width="15" height="15" />;
    }
    if (carClass === 'Diretoria') {
      return <img src={CarDiretoria} alt="Car Diretoria" width="15" height="15" />;
    }

    return null;
  };

  // Valores renderizados por linha, memoizados para o hook de medição (at-43).
  // `labelDe` é estável (useCallback no useEsteiras), então as dependências fecham.
  const getColumnValues = useCallback(
    (car: any) => [
      car.plate || '-',
      car.model || '-',
      car.responsibleName || '-',
      car.carClass || '-',
      String(car.daysInShop ?? '-'),
      labelDe(car.status),
      labelDe(car.pendingStatus),
    ],
    [labelDe],
  );

  const tableRows = useMemo(() => cars.map(getColumnValues), [cars, getColumnValues]);

  const { tableColumns, tableWidth } = useTableColumns({
    containerRef: tableScrollRef,
    headerCellsRef: headerRefs,
    headers,
    defaultWidths: defaultColumnWidths,
    rows: tableRows,
  });

  const hasAnyFilter = (filters: FilterParams) =>
    Object.values(filters).some((val) => {
      if (Array.isArray(val)) return val.length > 0;
      return Boolean(val);
    });

  const loadCars = useCallback(
    async (
      page: number,
      pageLimit: number,
      currentSortBy: string = 'createdAt',
      currentSortOrder: 'asc' | 'desc' = 'desc'
    ) => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetchCars(page, pageLimit, currentSortBy, currentSortOrder);
        setCars(response.cars);
        setTotalCars(response.total);
      } catch (e) {
        // at-64: lista vazia com a tela sumida não dizia nem o que falhou nem por quê.
        setCars([]);
        setTotalCars(0);
        setError(mensagemDeErro(e, 'Não foi possível carregar os veículos. Recarregue a página; se persistir, acione a TI.'));
        notify.errorFrom(e, 'Não foi possível carregar os veículos. Recarregue a página; se persistir, acione a TI.');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const loadCarsWithFilters = useCallback(
    async (
      filters: FilterParams,
      page: number,
      pageLimit: number,
      currentSortBy?: string,
      currentSortOrder?: 'asc' | 'desc'
    ) => {
      setLoading(true);
      setError(null);

      try {
        const response = await filterCars(
          filters.carClass,
          filters.carStatus,
          filters.pendingStatus,
          filters.approvalStatus,
          filters.damageType,
          page,
          pageLimit,
          currentSortBy,
          currentSortOrder
        );

        setCars(response.cars);
        setTotalCars(response.total);
      } catch (e) {
        // Lista filtrada vazia por erro é indistinguível de "nenhum veículo neste filtro".
        setCars([]);
        setTotalCars(0);
        setError(mensagemDeErro(e, 'Não foi possível aplicar os filtros. Recarregue a página; se persistir, acione a TI.'));
        notify.errorFrom(e, 'Não foi possível aplicar os filtros. Recarregue a página; se persistir, acione a TI.');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const loadSearchResults = useCallback(
    async (
      query: string,
      page: number = 1,
      currentSortBy: string = 'createdAt',
      currentSortOrder: 'asc' | 'desc' = 'desc'
    ) => {
      setLoading(true);
      setError(null);

      try {
        const response = await searchCars(
          query,
          page,
          limit,
          currentSortBy,
          currentSortOrder,
          undefined,
          // at-65 (BUG-V12): esta tela mostra entregues na listagem e no filtro — a busca
          // precisa concordar, senão a placa some ao ser pesquisada (decisão do usuário).
          true
        );

        // at-65 (BUG-V32): esta tela gravava o resultado e o termo da busca no slice da
        // RECEPÇÃO (`setSearchResults`/`setReduxCurrentPage`/`setLastSearchQuery`) e nunca
        // os lia de volta — escrita morta que contaminava a outra tela. O estado desta
        // tela é local (`cars`/`totalCars`).
        setCars(response.cars);
        setTotalCars(response.total);
      } catch (e) {
        // "Nenhum resultado" para uma placa que existe manda o usuário cadastrar de novo.
        setCars([]);
        setTotalCars(0);
        setError(mensagemDeErro(e, 'Não foi possível realizar a pesquisa. Recarregue a página; se persistir, acione a TI.'));
        notify.errorFrom(e, 'Não foi possível realizar a pesquisa. Recarregue a página; se persistir, acione a TI.');
      } finally {
        setLoading(false);
      }
    },
    // at-65: `dispatch` saiu da lista — a função não despacha mais nada (BUG-V32).
    [limit]
  );

  const resetAllFilters = () => {
    // at-65 (BUG-V32): o `resetFilters()` global saiu junto com os dispatches de filtro —
    // limpar o filtro desta tela zerava também o da Recepção.
    setActiveFilters({ ...EMPTY_FILTERS });
  };

  /**
   * Aplica a busca. **Só mexe em estado** — quem carrega é o efeito único abaixo.
   *
   * at-65 (BUG-V39): antes esta função carregava por conta própria E mudava estado que o
   * efeito observava, então cada busca disparava **duas** requisições (a explícita daqui e
   * a do efeito). Agora existe um só caminho de carga.
   *
   * at-65 (BUG-V42): buscar com o campo VAZIO era um "listar tudo" que, de carona, apagava
   * os filtros aplicados — quem limpava a busca para voltar à lista filtrada perdia o
   * filtro sem pedir. Campo vazio agora só desfaz a busca e devolve a lista ao filtro
   * que estiver valendo.
   */
  const triggerSearch = () => {
    const trimmedQuery = searchQuery.trim();

    dispatch(resetSearchResults());

    // Busca com termo é excludente com filtro (o backend não combina os dois).
    if (trimmedQuery) resetAllFilters();

    setAppliedSearchQuery(trimmedQuery);
    setCurrentPage(1);
  };

  const openFilterModal = () => {
    setFilterModalOpen(true);
  };

  const handleSortChange = (selectedSortBy: string, order: 'asc' | 'desc') => {
    dispatch(setSortOrderRedux({ sortBy: selectedSortBy, sortOrder: order }));
    setSortBy(selectedSortBy);
    setSortOrder(order);
    setIsFilterDropdownOpen(false);
  };

  const handleClickOutsideFilterDropdown = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsFilterDropdownOpen(false);
    }
  };

  const handleOpenStatusModal = (carId: string) => {
    setSelectedCarId(carId);
    setIsStatusModalOpen(true);
  };

  const handleOpenResponsiblesModal = (carId: string) => {
    setSelectedCarId(carId);
    setIsResponsiblesModalOpen(true);
  };

  const handleCloseStatusModal = () => {
    setIsStatusModalOpen(false);
    setSelectedCarId(null);
  };

  const handleCloseResponsiblesModal = () => {
    setIsResponsiblesModalOpen(false);
    setSelectedCarId(null);
  };

  const handleOpenChecklistHistoryModal = (carId: string, carPlate: string) => {
    setSelectedCarId(carId);
    setSelectedCarPlate(carPlate || '');
    setIsChecklistHistoryModalOpen(true);
  };

  const handleCloseChecklistHistoryModal = () => {
    setIsChecklistHistoryModalOpen(false);
    setSelectedCarId(null);
    setSelectedCarPlate('');
  };

  const handleOpenActionsModal = (carId: string) => {
    setSelectedCarId(carId);
    setIsActionsModalOpen(true);
  };

  const handleCloseActionsModal = () => {
    setIsActionsModalOpen(false);
    setSelectedCarId(null);
  };

  const openChecklistReadOnly = async (carId: string, checklistType?: 'ENTRADA' | 'SAIDA') => {
    try {
      let sessions: any[] = [];

      if (checklistType) {
        try {
          const typedSession = await getLatestChecklistSessionByCar(carId, {
            type: checklistType,
            status: 'COMPLETED',
          });
          sessions = typedSession ? [typedSession] : [];
        } catch {
          sessions = await getChecklistSessionsByCar(carId);
        }
      } else {
        sessions = await getChecklistSessionsByCar(carId);
      }

      const completedSessions = (sessions || []).filter(
        (session: any) => String(session.status || '').toUpperCase() === 'COMPLETED'
      );

      const targetSession = checklistType
        ? completedSessions.find(
            (session: any) => String(session.type || '').toUpperCase() === checklistType
          )
        : completedSessions[0];

      if (!targetSession) {
        notify.warning(
          checklistType
            ? `Checklist de ${ehFamiliaSaida(checklistType) ? 'saída' : 'entrada'} ainda não encontrado para este veículo.`
            : 'Checklist ainda não encontrado para este veículo.'
        );
        return;
      }

      setChecklistCarId(carId);
      setChecklistSessionId(targetSession.id);
      setChecklistTipoServico((targetSession.type || 'ENTRADA') as TipoServico);
      setChecklistReadOnly(true);
      setIsChecklistModalOpen(true);
    } catch (e: any) {
      notify.error(e?.message || 'Checklist ainda não encontrado para este veículo.');
    }
  };

  const handleOpenReportModal = () => {
    setIsReportModalOpen(true);
  };

  const handleCloseReportModal = () => {
    setIsReportModalOpen(false);
  };

  const handleApplyFilters = (filters: FilterParams) => {
    const carClass = filters.carClass || [];
    const carStatus = filters.carStatus || [];
    const pendingStatus = filters.pendingStatus || [];

    dispatch(resetSearchResults());
    // at-65 (BUG-V32): sem dispatch para o slice da Recepção — era o que vazava o filtro
    // desta tela para a dela (e vice-versa).

    setSearchQuery('');
    setActiveFilters({
      carClass,
      carStatus,
      pendingStatus,
      approvalStatus: undefined,
      damageType: [],
    });

    setCurrentPage(1);
    setFilterModalOpen(false);
  };

  /**
   * Situação de checklist do veículo SELECIONADO (at-65, BUG-V33).
   *
   * Antes este efeito consultava `GET /checklists/cars/:id/sessions` **de cada veículo da
   * página** — até 50 requisições —, e o efeito dependia de `cars`, então tudo era refeito
   * a cada troca de página, filtro, ordenação e (antes do BUG-V34) **cada tecla da busca**.
   * Tudo isso para habilitar dois botões dentro de um modal que abre **um** veículo por vez.
   *
   * Agora a consulta acontece na seleção: uma requisição, no momento em que ela é usada.
   */
  useEffect(() => {
    if (!selectedCarId || !isActionsModalOpen) return;

    let isMounted = true;

    const loadChecklistAvailability = async () => {
      try {
        const sessions = await getChecklistSessionsByCar(selectedCarId);
        if (!isMounted) return;

        const completedSessions = (sessions || []).filter(
          (session: any) => String(session.status || '').toUpperCase() === 'COMPLETED'
        );

        setChecklistAvailabilityByCarId((prev) => ({
          ...prev,
          [selectedCarId]: {
            entrada: completedSessions.some(
              (session: any) => ehFamiliaEntrada(String(session.type || '').toUpperCase())
            ),
            saida: completedSessions.some(
              (session: any) => ehFamiliaSaida(String(session.type || '').toUpperCase())
            ),
          },
        }));
      } catch (e) {
        if (!isMounted) return;
        // Botão desabilitado por falha é indistinguível de "não existe checklist" (at-60).
        setChecklistAvailabilityByCarId((prev) => ({
          ...prev,
          [selectedCarId]: { entrada: false, saida: false },
        }));
        // ⚠️ Texto fixo, não `errorFrom`: num 403 o extrator devolveria "Você não tem
        // permissão" e engoliria a consequência, que é o que orienta o usuário.
        notify.error(
          'Não foi possível carregar a situação dos checklists deste veículo. As opções de checklist podem aparecer indisponíveis. Recarregue a página; se persistir, acione a TI.',
          { toastId: 'tela-controle-checklist-disponibilidade' }
        );
      }
    };

    void loadChecklistAvailability();

    return () => {
      isMounted = false;
    };
  }, [selectedCarId, isActionsModalOpen]);

  useEffect(() => {
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutsideFilterDropdown);
    } else {
      document.removeEventListener('mousedown', handleClickOutsideFilterDropdown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutsideFilterDropdown);
    };
  }, [isDropdownOpen]);

  const handleCarUpdated = () => {
    const currentSortBy = sortBy ?? 'createdAt';
    const currentSortOrder = sortOrder;

    // at-65: termo APLICADO, não o rascunho — era isso que fazia cada tecla buscar.
    if (appliedSearchQuery) {
      void loadSearchResults(appliedSearchQuery, currentPage, currentSortBy, currentSortOrder);
      return;
    }

    if (hasAnyFilter(activeFilters) || sortBy) {
      void loadCarsWithFilters(
        activeFilters,
        currentPage,
        limit,
        currentSortBy,
        currentSortOrder
      );
      return;
    }

    void loadCars(currentPage, limit, currentSortBy, currentSortOrder);
  };

  useEffect(() => {
    const currentSortBy = sortBy ?? 'createdAt';
    const currentSortOrder = sortOrder;

    // at-65: termo APLICADO, não o rascunho — era isso que fazia cada tecla buscar.
    if (appliedSearchQuery) {
      void loadSearchResults(appliedSearchQuery, currentPage, currentSortBy, currentSortOrder);
      return;
    }

    if (hasAnyFilter(activeFilters) || sortBy) {
      void loadCarsWithFilters(
        activeFilters,
        currentPage,
        limit,
        currentSortBy,
        currentSortOrder
      );
      return;
    }

    void loadCars(currentPage, limit, currentSortBy, currentSortOrder);
    // at-65: `appliedSearchQuery` no lugar de `searchQuery` — este é o ÚNICO caminho de
    // carga da tela, e ele reage ao termo aplicado, à página, ao limite, à ordenação e aos
    // filtros. Digitar não entra aqui.
  }, [
    activeFilters,
    currentPage,
    limit,
    sortBy,
    sortOrder,
    appliedSearchQuery,
    loadCars,
    loadCarsWithFilters,
    loadSearchResults,
  ]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const renderPagination = () => {
    if (totalPages === 1) {
      return (
        <div key={1} className="page-number active">
          1
          <div className="Ellipse14" />
        </div>
      );
    }

    const pages = [];
    const maxVisiblePages = 10;

    pages.push(
      <div
        key={1}
        className={`page-number ${currentPage === 1 ? 'active' : ''}`}
        onClick={() => setCurrentPage(1)}
      >
        1
        {currentPage === 1 && <div className="Ellipse14" />}
      </div>
    );

    if (currentPage > maxVisiblePages - 4) {
      pages.push(
        <span key="left-ellipsis" className="ellipsis">
          ...
        </span>
      );
    }

    const startPage = Math.max(
      2,
      Math.min(
        currentPage - Math.floor((maxVisiblePages - 2) / 2),
        totalPages - (maxVisiblePages - 2)
      )
    );

    const endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 3);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <div
          key={i}
          className={`page-number ${currentPage === i ? 'active' : ''}`}
          onClick={() => setCurrentPage(i)}
        >
          {i}
          {currentPage === i && <div className="Ellipse14" />}
        </div>
      );
    }

    if (endPage < totalPages - 1) {
      pages.push(
        <span key="right-ellipsis" className="ellipsis">
          ...
        </span>
      );
    }

    pages.push(
      <div
        key={totalPages}
        className={`page-number ${currentPage === totalPages ? 'active' : ''}`}
        onClick={() => setCurrentPage(totalPages)}
      >
        {totalPages}
        {currentPage === totalPages && <div className="Ellipse14" />}
      </div>
    );

    return pages;
  };

  const helpSections: ComoFuncionaSecao[] = [
    {
      titulo: 'O que esta tela mostra',
      conteudo: (
        <p>
          A visão administrativa de <strong>todos os veículos da oficina</strong>, uma linha por atendimento, com
          colaboradores, dias na oficina e a esteira atual e pendente. É uma tela de <strong>consulta</strong>: os
          checklists abrem sempre em leitura, e a única ação operacional disponível é o Controle de Liberação (para
          administradores). Os números refletem o momento em que a tela foi carregada — recarregue a página para
          atualizar. Além dos administradores e da TI, o <strong>Chefe de Oficina</strong> alcança esta tela desde
          17/09/2026, para gerar o Relatório de Veículos.
        </p>
      ),
    },
    {
      titulo: 'Busca, filtros e ordenação',
      conteudo: (
        <p>
          A busca do topo encontra por <strong>placa, responsável ou número do evento</strong>.{' '}
          <strong>Filtrar</strong> combina classe, esteira atual e esteira pendente. Atenção:{' '}
          <strong>pesquisar limpa os filtros aplicados</strong>, e aplicar filtros limpa a pesquisa — use um de cada
          vez. <strong>Ordenar por</strong> reordena por placa ou data de entrada.
        </p>
      ),
    },
    {
      titulo: 'O selo "Inacabado"',
      conteudo: (
        <p>
          Na coluna <strong>Esteira atual</strong>, o selo roxo <strong>Inacabado</strong> lista as esteiras em que
          o serviço do veículo começou e foi interrompido sem conclusão — uma pendência que pode ser retomada
          quando o veículo voltar àquela etapa. O roxo é a mesma cor usada para serviço inacabado no Painel Geral.
        </p>
      ),
    },
    {
      titulo: 'O modal da placa',
      conteudo: (
        <p>
          O clique na placa abre o atendimento completo: dados, situação da{' '}
          <strong>Liberação Administrativa</strong>, observações, galeria de imagens e os históricos —{' '}
          <strong>Histórico de Esteiras</strong> (por onde o veículo passou e quanto tempo ficou),{' '}
          <strong>Histórico de Responsáveis</strong> e <strong>Histórico de Checklists</strong>. Os checklists
          abrem em leitura, com PDF e compartilhamento. O botão <strong>Controle de Liberação</strong>, visível
          para administradores, concede ou remove a liberação do veículo por esteira.
        </p>
      ),
    },
    {
      titulo: 'Relatório de Veículos',
      conteudo: (
        <>
          <p>
            <strong>Gerar Relatório de Veículos</strong> exporta os dados da oficina em{' '}
            <strong>CSV</strong> (para planilha) ou <strong>PDF</strong> (com a identidade visual da
            Atual, para imprimir ou enviar). A geração é de <strong>administradores e do Chefe de
            Oficina</strong>.
          </p>
          <p>
            Antes das colunas, escolha <strong>o que o relatório traz</strong> — e é essa escolha que
            define o que <em>uma linha</em> significa:
          </p>
          <ul>
            <li>
              <strong>Atendimento</strong> — uma linha por veículo: identificação, proprietário,
              evento, etapa atual, motivo da pendência, prazos, liberação, observações e o resumo do
              que foi feito (comentários, sub-tarefas concluídas, tempo trabalhado, checklists).
            </li>
            <li>
              <strong>Etapas e tempos</strong> — uma linha por passagem do veículo por uma etapa, com
              o tempo de cada passagem e quem trabalhou nela. Um veículo que voltou para a mesma
              etapa aparece em mais de uma linha.
            </li>
            <li>
              <strong>Feedback</strong> — uma linha por comentário ou resposta, com quem escreveu e
              quando. Comentário do tipo &quot;vale para o veículo&quot; acompanha a placa, então
              pode ser de um atendimento anterior — a coluna{' '}
              <strong>De Atendimento Anterior</strong> diz quando é o caso.
            </li>
          </ul>
          <p>
            O <strong>período filtra a data de entrada na oficina</strong>, não a data em que o
            veículo foi cadastrado; sem datas, o relatório traz todos. No PDF há um limite de colunas
            que caibam legíveis na folha: escolhendo mais do que isso, a tela avisa antes de gerar e
            sugere o CSV — o arquivo nunca sai com colunas cortadas em silêncio.
          </p>
        </>
      ),
    },
    ...(isTi
      ? [
          {
            titulo: 'Seu acesso (TI)',
            conteudo: (
              <p>
                O perfil de TI usa esta tela em <strong>modo consulta</strong>: sem o relatório de veículos e sem o
                Controle de Liberação, que são de administradores. Todos os históricos e checklists ficam
                disponíveis em leitura.
              </p>
            ),
          },
        ]
      : []),
  ];

  return (
    <div
      className="Frame1000004012"
      style={{
        height: loading ? '100vh' : 'fit-content',
        width: '100%',
        display: 'flex',
        justifyContent: loading ? 'center' : 'space-between',
        alignItems: loading ? 'center' : 'flex-start',
        backgroundColor: loading ? '#808080' : 'transparent',
      }}
    >
      {loading && <FullScreenLoader />}

      {!loading && (
        <div className="ConteDo tela-de-controle-page recepcao-conte-do">
          <div className="recepcao-header-row">
            <div className="ChefeDeOficina" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              Tela de Controle
              <button
                type="button"
                className="help-circle-btn"
                title="Como Funciona"
                onClick={() => setIsHelpOpen(true)}
              >
                ?
              </button>
            </div>
            <div className="recepcao-search-bar">
              <div className="ItemSearch" onClick={() => void triggerSearch()}>
                <img
                  className="imgIcon"
                  src={SearchIcon}
                  alt="Ícone de busca"
                  width="20"
                  height="20"
                />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    void triggerSearch();
                  }
                }}
                placeholder="Pesquise por placa, responsável ou nº do evento"
                className="recepcao-search-input"
              />
            </div>
          </div>

          {/*
            at-64 (BUG-V27): antes o erro escondia a tela inteira (`!loading && !error`) num
            parágrafo com classe `error`, que não existe em CSS nenhum — texto preto solto
            no lugar da tabela. Agora a tela continua de pé (busca, filtros e o que já estava
            carregado) e o erro aparece como faixa, com a explicação vinda do backend.
          */}
          {error && (
            <div className="tela-de-controle-error" role="alert">
              {error}
            </div>
          )}

          <div className="VisualizarOrdenar">
            <div
              className="Frame1000004037"
              style={{
                alignSelf: 'stretch',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: 12,
                display: 'flex',
                flexWrap: 'wrap',
              }}
            >
              <div className="CtaBig">
                <div className="Frame1000003987">
                  <div className="ComponentName">Itens por página:</div>
                  <select
                    id="limit"
                    value={limit}
                    onChange={(e) => {
                      setLimit(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="ComponentNameImgIcon"
                    style={{ cursor: 'pointer' }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>

              <div className="CtaBig MouseHover" onClick={openFilterModal}>
                <div className="Frame1000003987">
                  <img
                    src={Filter}
                    alt="Filtro"
                    width="19.8"
                    height="19.8"
                  />
                  <div className="ComponentName">Filtrar</div>
                </div>
              </div>

              <div className="CtaBig MouseHover" onClick={() => setIsFilterDropdownOpen(!isDropdownOpen)}>
                <div className="Frame1000003987">
                  <img
                    src={ArrowDown}
                    alt="Ordenar"
                    width="12.8"
                    height="12.8"
                  />
                  <div className="ComponentName">Ordenar por</div>
                </div>

                {isDropdownOpen && (
                  <div className="DropdownMenu" ref={dropdownRef} onClick={(e) => e.stopPropagation()}>
                    <div
                      className="DropdownOption"
                      onClick={() => {
                        setIsFilterDropdownOpen(false);
                        handleSortChange('plate', 'asc');
                      }}
                    >
                      Placa
                    </div>
                    <div
                      className="DropdownOption"
                      onClick={() => {
                        setIsFilterDropdownOpen(false);
                        handleSortChange('entryDate', 'desc');
                      }}
                    >
                      Data de entrada mais recente
                    </div>
                    <div
                      className="DropdownOption"
                      onClick={() => {
                        setIsFilterDropdownOpen(false);
                        handleSortChange('entryDate', 'asc');
                      }}
                    >
                      Data de entrada mais antiga
                    </div>
                  </div>
                )}
              </div>
            </div>

            {!isTi && (
              <div onClick={handleOpenReportModal} className="CtaSmall MouseHover">
                <div className="ComponentName">Gerar Relatório de Veículos</div>
              </div>
            )}
          </div>

          <div className="QuantidadeDeCarros">
            <div
              className="Frame1000004037"
              style={{
                alignSelf: 'stretch',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: 12,
                display: 'flex',
                flexWrap: 'wrap',
              }}
            >
              <div className="CtaQuantidadeDeCarros">
                <div className="Frame1000003987">
                  <div className="ComponentName">
                    {totalCars === 0
                      ? '0 carros'
                      : `${startCarIndex}-${endCarIndex} de ${totalCars} carros`}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              width: '100%',
              paddingTop: 30,
              background: 'var(--tema-superficie, white)',
              borderRadius: 16,
              border: '1px var(--tema-linha-2, #CACACA) solid',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
              display: 'flex',
              marginBottom: 20,
            }}
          >
            <div className="TabelaScroll" ref={tableScrollRef}>
              <div
                className="TabelaContent"
                style={{
                  ['--table-columns' as any]: tableColumns,
                  width: tableWidth,
                  minWidth: tableWidth,
                }}
              >
                <div className="Tabela TabelaHeader">
                  <div className="Placa">
                    <div className="TTulo">
                      {headers.map((header, index) => (
                        <div
                          key={header}
                          className="Opcoes"
                          ref={(el) => {
                            headerRefs.current[index] = el;
                          }}
                        >
                          {header}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {cars.length === 0 ? (
                  <div className="Tabela">
                    <div className="Item" style={{ gridColumn: `1 / span ${headers.length}` }}>
                      <div className="Data">Nenhum carro encontrado.</div>
                    </div>
                  </div>
                ) : (
                  cars.map((car) => {
                    return (
                      <div key={car.id} className="Tabela">
                        <div className="Item">
                          <div
                            className="Data"
                            style={{
                              cursor: 'pointer',
                              color: '#ea5d0e',
                              textDecoration: 'underline',
                            }}
                            onClick={() => handleOpenActionsModal(car.id)}
                          >
                            {car.plate || '-'}
                          </div>
                        </div>

                        <div className="Item">
                          <div className="Data">{car.model || '-'}</div>
                        </div>

                        <div className="Item">
                          <div className="Data">{car.responsibleName || '-'}</div>
                        </div>

                        <div className="Item">
                          <div
                            className="Data"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                            }}
                          >
                            {getCarClassIcon(car.carClass)}
                            <span>{car.carClass || '-'}</span>
                          </div>
                        </div>

                        <div className="Item">
                          <div className="Data">{car.daysInShop ?? '-'}</div>
                        </div>

                        <div className="Item">
                          <div className="Data" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <span>{labelDe(car.status)}</span>
                            {/* at-46: serviço inacabado é pendência visível também aqui. */}
                            {(() => {
                              const inacabados: ServicoInacabado[] = (car.servicosInacabados ?? []).filter(
                                (s: ServicoInacabado) => s.estado === 'PAUSADA',
                              );
                              if (inacabados.length === 0) return null;
                              const nomes = inacabados
                                .map((s: ServicoInacabado) => s.esteiraNome || labelDe(s.esteiraId))
                                .join(', ');
                              return (
                                <span className="servico-inacabado-badge" title={`Serviço inacabado em: ${nomes}`}>
                                  Inacabado: {nomes}
                                </span>
                              );
                            })()}
                          </div>
                        </div>

                        <div className="Item">
                          <div className="ChefeDeOficinaPendingCell">
                            <span
                              className="Data"
                              style={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '100%',
                                display: 'inline-block',
                              }}
                            >
                              {labelDe(car.pendingStatus)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div className="PaginaO">
              <div
                className={`Frame1000003950 ${currentPage === 1 ? 'disabled' : ''}`}
                onClick={() => {
                  if (currentPage > 1) {
                    setCurrentPage(currentPage - 1);
                  }
                }}
                style={{ cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
              >
                <img
                  className="imgIcon"
                  src={currentPage === 1 ? ArrowLeftGray : ArrowLeftOrange}
                  alt="Seta para Esquerda"
                  width="24"
                  height="24"
                />
                <div
                  className="Anterior"
                  style={{ color: currentPage === 1 ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)' }}
                >
                  Anterior
                </div>
              </div>

              <div className="Group21">
                <div className="Group20">{renderPagination()}</div>
              </div>

              <div
                className={`Frame1000003949 ${
                  currentPage === totalPages ? 'disabled' : ''
                }`}
                onClick={() => {
                  if (currentPage < totalPages) {
                    setCurrentPage(currentPage + 1);
                  }
                }}
                style={{
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                }}
              >
                <div
                  className="PrXima"
                  style={{ color: currentPage === totalPages ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)' }}
                >
                  Próxima
                </div>
                <img
                  className="imgIcon"
                  src={currentPage === totalPages ? ArrowRightGray : ArrowRightOrange}
                  alt="Seta para Direita"
                  width="24"
                  height="24"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedCarId && (
        <>
          <StatusHistoryModal
            isOpen={isStatusModalOpen}
            onCancel={handleCloseStatusModal}
            carId={selectedCarId}
            sidebarRef={sidebarRef}
            isSidebarOpen={isStatusSidebarOpen}
          />

          <ResponsiblesHistoryModal
            isOpen={isResponsiblesModalOpen}
            onCancel={handleCloseResponsiblesModal}
            carId={selectedCarId}
            sidebarRef={sidebarRef}
            isSidebarOpen={isStatusSidebarOpen}
          />

          <ChecklistHistoryModal
            isOpen={isChecklistHistoryModalOpen}
            onCancel={handleCloseChecklistHistoryModal}
            carId={selectedCarId || ''}
            carPlate={selectedCarPlate}
            sidebarRef={sidebarRef}
            isSidebarOpen={isStatusSidebarOpen}
          />

          <TelaDeControleActionsModal
            isOpen={isActionsModalOpen}
            carId={selectedCarId}
            onCancel={handleCloseActionsModal}
            onOpenStatusModal={(id) => {
              handleCloseActionsModal();
              handleOpenStatusModal(id);
            }}
            onOpenResponsiblesModal={(id) => {
              handleCloseActionsModal();
              handleOpenResponsiblesModal(id);
            }}
            onOpenChecklist={(id, type) => {
              handleCloseActionsModal();
              void openChecklistReadOnly(id, type);
            }}
            onOpenChecklistHistory={(id, plate) => {
              handleCloseActionsModal();
              handleOpenChecklistHistoryModal(id, plate);
            }}
            checklistAvailability={{
              entrada: checklistAvailabilityByCarId[selectedCarId]?.entrada || false,
              saida: checklistAvailabilityByCarId[selectedCarId]?.saida || false,
            }}
            sidebarRef={sidebarRef}
            onCarUpdated={handleCarUpdated}
          />
        </>
      )}

      <CarsReportModal
        isOpen={isReportModalOpen}
        onCancel={handleCloseReportModal}
        sidebarRef={sidebarRef}
        isSidebarOpen={isStatusSidebarOpen}
      />

      {/* at-65 (BUG-V43): montagem condicional + filtros em vigor, para o modal reabrir
          mostrando o que está valendo em vez de sempre em branco. */}
      {filterModalOpen && (
        <FilterCarModal
          onCancel={() => setFilterModalOpen(false)}
          onApplyFilters={handleApplyFilters}
          availableClasses={availableClasses}
          sidebarRef={sidebarRef}
          isSidebarOpen={isStatusSidebarOpen}
          filtrosAplicados={{
            carClass: activeFilters.carClass,
            carStatus: activeFilters.carStatus,
            pendingStatus: activeFilters.pendingStatus,
          }}
        />
      )}

      <ChecklistModal
        isOpen={isChecklistModalOpen}
        onClose={() => {
          setIsChecklistModalOpen(false);
          setChecklistCarId(null);
          setChecklistSessionId(null);
          setChecklistReadOnly(true);
        }}
        carId={checklistCarId || ''}
        sessionId={checklistSessionId || ''}
        tipoServico={checklistTipoServico}
        readOnly={checklistReadOnly}
        // at-65 (BUG-V11): esta é tela de CONSULTA — o `readOnly` não bastava, porque o
        // botão "Editar checklist" aparecia de todo modo para quem tem a permissão.
        permitirEdicao={false}
      />

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona — Tela de Controle"
        secoes={helpSections}
      />
    </div>
  );
};

export default TelaDeControle;
